import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Breadcrumb } from "../Breadcrumb";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import API from "../api/api01";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Plus, Edit, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string;
  category: string;
  copies: number;
  available_copies: number;
  description: string;
}

export function ViewBooks() {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({ title: "", author: "", isbn: "", category: "", copies: "0", description: "" });

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const response = await API.get("/superadmin/library/all");
      setBooks(response.data);
    } catch (error) {
      toast.error("Failed to load library books");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const filteredBooks = books.filter(
    (book) =>
      book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.isbn?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = async () => {
    if (!selectedBook || !formData.title || !formData.author) {
      toast.error("Title and Author are required");
      return;
    }

    setSubmitting(true);
    try {
      await API.put(`/superadmin/library/books/${selectedBook.id}`, {
        title: formData.title,
        author: formData.author,
        isbn: formData.isbn,
        category: formData.category,
        total_copies: parseInt(formData.copies),
        available_copies: parseInt(formData.copies), // Simplification: resetting availability on edit
        description: formData.description
      });
      
      toast.success("Book updated successfully!");
      setIsEditDialogOpen(false);
      setSelectedBook(null);
      fetchBooks();
    } catch (error) {
      toast.error("Failed to update book");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBook) return;

    try {
      await API.delete(`/superadmin/library/books/${selectedBook.id}`);
      toast.success("Book removed successfully!");
      setDeleteDialogOpen(false);
      setSelectedBook(null);
      fetchBooks();
    } catch (error) {
      toast.error("Failed to delete book");
    }
  };

  const openEditDialog = (book: Book) => {
    setSelectedBook(book);
    setFormData({
      title: book.title || "",
      author: book.author || "",
      isbn: book.isbn || "",
      category: book.category || "",
      copies: book.copies?.toString() || "1",
      description: book.description || ""
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (book: Book) => {
    setSelectedBook(book);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">Library Catalog</h1>
            <Breadcrumb items={[{ label: "Library" }, { label: "View Books" }]} className="mt-2" />
          </div>

          <Button onClick={() => navigate(PATHS.ADD_LIBRARY)} className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 px-6">
            <Plus className="h-4 w-4 mr-2" />
            <span className="text-sm font-bold uppercase tracking-wider">Add Book</span>
          </Button>
        </div>

        {/* Search */}
        <Card className="bg-card border-white/10 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search books by title, author, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background border-white/5 text-sm font-medium"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card border-white/10 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">
              Manage Books {loading ? "" : `(${filteredBooks.length})`}
            </CardTitle>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                <p className="text-sm font-medium text-muted-foreground animate-pulse">Fetching library catalog...</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Book ID</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Title</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Author</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">ISBN</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Category</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 text-center">Copies</TableHead>
                    <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBooks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground font-medium italic">
                        No books found matching your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBooks.map((book) => (
                      <TableRow key={book.id} className="hover:bg-muted/40 transition group border-white/5">
                        <TableCell className="text-[12px] font-mono text-slate-500">#{book.id.slice(0, 8)}...</TableCell>
                        <TableCell className="text-[13px] font-bold text-slate-900 dark:text-white">{book.title}</TableCell>
                        <TableCell className="text-[13px] font-medium text-slate-600 dark:text-slate-400">{book.author}</TableCell>
                        <TableCell className="text-[12px] font-mono text-slate-500">{book.isbn}</TableCell>
                        <TableCell className="text-[13px] font-medium text-slate-600 dark:text-slate-400">{book.category}</TableCell>
                        <TableCell className="text-[13px] font-bold text-slate-900 dark:text-white text-center">
                          <span className={book.available_copies < book.copies ? "text-orange-500" : "text-green-500"}>
                            {book.available_copies}
                          </span>
                          <span className="text-muted-foreground mx-1 text-xs">/</span>
                          {book.copies}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(book)} className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-50/50">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(book)} className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50/50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="bg-card border border-white/10 shadow-xl max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Edit Book Details</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Title</Label>
                  <Input name="title" value={formData.title} onChange={handleChange} placeholder="Book title" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Author</Label>
                  <Input name="author" value={formData.author} onChange={handleChange} placeholder="Author name" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">ISBN</Label>
                  <Input name="isbn" value={formData.isbn} onChange={handleChange} placeholder="978-XXXXXXXXXX" className="rounded-xl font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Category</Label>
                  <Input name="category" value={formData.category} onChange={handleChange} placeholder="e.g. Science, Tech" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Total Copies</Label>
                  <Input name="copies" type="number" value={formData.copies} onChange={handleChange} className="rounded-xl font-bold" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Full Description</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange} placeholder="Book summary..." rows={3} className="rounded-xl resize-none" />
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 border-t border-white/5 pt-4">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="rounded-xl h-10 px-6 font-bold uppercase tracking-widest text-xs">Cancel</Button>
              <Button 
                onClick={handleEdit} 
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-10 px-8 font-bold uppercase tracking-widest text-xs shadow-lg shadow-blue-500/20"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border border-white/10 shadow-2xl rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl">Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove <strong>{selectedBook?.title}</strong> from the catalog? This action is permanent and cannot be reversed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl font-bold uppercase tracking-widest text-xs h-10 px-6">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700 rounded-xl px-8 font-bold uppercase tracking-widest text-xs h-10 shadow-lg shadow-red-500/20">
                Delete Book
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
